import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { WebAnalyticsProvider } from "./context";
import { configService } from "./services";
import Hls from 'hls.js';



import "./amplify-scoped.scss";
import "./index.css";

if (window && !(window as any).Hls) {
    (window as any).Hls = Hls;
}

class CembooMediaLibrary {
    private CLIENT_ID: string;
    private OUTLET_ID: string;
    private FETCH_BY_DATE: string;
    private ENABLE_FILTER_CONTROLS: boolean;
    private ENABLE_RATING_CONTROLS: boolean;
    private ENABLE_SHARE_CONTROLS: boolean;
    private ENABLE_ANALYTICS: boolean;
    private MEDIA_PER_PAGE: number;
    private ENABLE_AUTOPLAY: boolean;
    private IS_ENABLED: boolean = true;
    private SHOW_PLAYLISTS: boolean;
    private HIDE_DEFAULT_VOD: boolean;
    private ENABLE_COMPETITION_MODE: boolean;
    private STREAM_ENABLED: boolean;
    private STREAM_TAB_CONTROLS: boolean;
    private STREAM_SHARE_CONTROLS: boolean;
    private DISABLE_FULLSCREEN_PLAYER: boolean = false;
    private PREVIEW_MODE: boolean = false;
    private PROFILE_ID: string = "";
    private VERSION_ID: string | undefined = undefined;
    private ISOLATE_PLAYLIST_ID: string = "";
    private THEME: "light" | "dark" = "light";
    private USE_ID_MOUNT: boolean = false;


    constructor(
        private clientId: string,
        private fetchByDate: string,
        private enableFilterControls: boolean = false,
        private enableRatingControls: boolean = false,
        private enableShareControls: boolean = false,
        private enableAnalytics: boolean = true,
        private showPlaylists: boolean = false,
        private hideDefaultVOD: boolean = false,
        private enableCompetitionMode: boolean = false,
        private enableAutoplay: boolean = false,
        private streamEnabled: boolean = false,
        private streamTabControls: boolean = true,
        private streamShareControls: boolean = true,
        private disableFullscreenPlayer: boolean = false,
        private outletId: string = "default",
        private previewMode: boolean = false,
        private profileId: string = "",
        private versionId?: string,
        private theme: "light" | "dark" = "light",
        private useIdMount: boolean = true,
    ) {
        console.log("App Initiated", process.env.REACT_APP_MEDIA_SERVICE_ENDPOINT);
        if (process.env.REACT_APP_MEDIA_SERVICE_ENDPOINT) {
            console.log("Env Variables loaded");
        } else {
            console.log("Error while loading env variables");
        }

        this.CLIENT_ID = clientId;
        this.OUTLET_ID = outletId;
        this.FETCH_BY_DATE = fetchByDate;
        this.ENABLE_FILTER_CONTROLS = enableFilterControls;
        this.ENABLE_RATING_CONTROLS = enableRatingControls;
        this.ENABLE_SHARE_CONTROLS = enableShareControls;
        this.ENABLE_ANALYTICS = enableAnalytics;
        this.SHOW_PLAYLISTS = showPlaylists;
        this.MEDIA_PER_PAGE = 16;
        this.HIDE_DEFAULT_VOD = hideDefaultVOD;
        this.ENABLE_COMPETITION_MODE = enableCompetitionMode;
        this.ENABLE_AUTOPLAY = enableAutoplay;
        this.STREAM_ENABLED = true;
        this.STREAM_TAB_CONTROLS = streamTabControls;
        this.STREAM_SHARE_CONTROLS = streamShareControls;
        this.DISABLE_FULLSCREEN_PLAYER = disableFullscreenPlayer;
        this.PREVIEW_MODE = previewMode;
        this.PROFILE_ID = this.PREVIEW_MODE ? this.profileId : "";
        this.VERSION_ID = this.PREVIEW_MODE ? this.versionId : undefined;
        this.THEME = theme;
        this.USE_ID_MOUNT = useIdMount;
    }

    setPlaylistId(playlistId: string) {
        this.ISOLATE_PLAYLIST_ID = playlistId;
    }

    init() {


        configService.getLibraryConfig(this.CLIENT_ID).then(({ mediaConfig, streamConfig }) => {
            //Media configs
            if (mediaConfig) {
                console.log("Media Configs loaded from API", mediaConfig);

                this.ENABLE_FILTER_CONTROLS = mediaConfig.filterControls !== undefined ? mediaConfig.filterControls : this.ENABLE_FILTER_CONTROLS;
                this.ENABLE_RATING_CONTROLS = mediaConfig.ratingControls !== undefined ? mediaConfig.ratingControls : this.ENABLE_RATING_CONTROLS;
                this.ENABLE_SHARE_CONTROLS = mediaConfig.shareControls !== undefined ? mediaConfig.shareControls : this.ENABLE_SHARE_CONTROLS;
                this.FETCH_BY_DATE = mediaConfig.fetchByDate !== undefined ? String(mediaConfig.fetchByDate) : this.FETCH_BY_DATE;
                this.MEDIA_PER_PAGE = mediaConfig.mediaPerPage !== undefined ? mediaConfig.mediaPerPage : this.MEDIA_PER_PAGE;
                this.IS_ENABLED = mediaConfig.enabled !== undefined ? mediaConfig.enabled : this.IS_ENABLED;
                this.ENABLE_ANALYTICS = mediaConfig.analytics !== undefined ? mediaConfig.analytics : this.ENABLE_ANALYTICS;
                this.SHOW_PLAYLISTS = mediaConfig.showPlaylists !== undefined ? mediaConfig.showPlaylists : this.SHOW_PLAYLISTS;
                this.HIDE_DEFAULT_VOD = mediaConfig.hideDefaultVOD !== undefined ? mediaConfig.hideDefaultVOD : this.HIDE_DEFAULT_VOD;
                this.ENABLE_COMPETITION_MODE = mediaConfig.enableCompetitionMode !== undefined ? mediaConfig.enableCompetitionMode : this.ENABLE_COMPETITION_MODE;
                this.ENABLE_AUTOPLAY = mediaConfig.enableAutoplay !== undefined ? mediaConfig.enableAutoplay : this.ENABLE_AUTOPLAY;
                this.DISABLE_FULLSCREEN_PLAYER = mediaConfig.disableFullscreenPlayer !== undefined ? mediaConfig.disableFullscreenPlayer : this.DISABLE_FULLSCREEN_PLAYER;
                this.THEME = mediaConfig.enableDarkMode ? "dark" : "light";
            }
            //Live stream configs
            if (streamConfig) {

                this.STREAM_TAB_CONTROLS = streamConfig.tabControls !== undefined ? streamConfig.tabControls : this.STREAM_TAB_CONTROLS;
                this.STREAM_SHARE_CONTROLS = streamConfig.shareControls !== undefined ? streamConfig.shareControls : this.STREAM_SHARE_CONTROLS;
                this.STREAM_ENABLED = streamConfig.enabled !== undefined ? streamConfig.enabled : this.STREAM_ENABLED;
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            const mountPromise = this.USE_ID_MOUNT
                ? checkForElementLoaded("#cemboo-media-library", 5000)
                : checkForTextLoaded("cemboo-media-library", 5000);

            mountPromise.then(() => {
                    const element = this.USE_ID_MOUNT
                        ? document.getElementById("cemboo-media-library") as HTMLElement
                        : findAndReplaceTextNode("cemboo-media-library");
                    if (element) {
                        const root = ReactDOM.createRoot(element);
                        root.render(
                            <React.StrictMode>
                                <WebAnalyticsProvider enableAnalytics={this.ENABLE_ANALYTICS} clientId={this.CLIENT_ID}>
                                    <App
                                        clientId={this.CLIENT_ID}
                                        outletId={this.OUTLET_ID}
                                        fetchByDate={this.FETCH_BY_DATE}
                                        enableFilterControls={this.ENABLE_FILTER_CONTROLS}
                                        enableRatingControls={this.ENABLE_RATING_CONTROLS}
                                        enableShareControls={this.ENABLE_SHARE_CONTROLS}
                                        mediaPerPage={this.MEDIA_PER_PAGE}
                                        showPlaylists={this.SHOW_PLAYLISTS}
                                        hideDefaultVOD={this.HIDE_DEFAULT_VOD}
                                        enabled={this.IS_ENABLED}
                                        enableCompetitionMode={this.ENABLE_COMPETITION_MODE}
                                        enableAutoplay={this.ENABLE_AUTOPLAY}
                                        streamTabControls={this.STREAM_TAB_CONTROLS}
                                        streamShareControls={this.STREAM_SHARE_CONTROLS}
                                        streamEnabled={this.STREAM_ENABLED}
                                        disableFullscreenPlayer={this.DISABLE_FULLSCREEN_PLAYER}
                                        previewMode={this.PREVIEW_MODE}
                                        profileId={this.PROFILE_ID}
                                        versionId={this.VERSION_ID}
                                        isolatePlaylistId={this.ISOLATE_PLAYLIST_ID}
                                        theme={this.THEME}
                                    />
                                </WebAnalyticsProvider>
                            </React.StrictMode>
                        );
                    }
                });
        });
    }
}

function findAndReplaceTextNode(searchText: string): HTMLElement | null {
    const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null
    );

    let node: Node | null;
    while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim() === searchText) {
            const container = document.createElement('div');
            container.id = 'cemboo-media-library-container';
            node.parentNode?.replaceChild(container, node);
            return container;
        }
    }
    return null;
}

function checkForElementLoaded(querySelector: string, timeout: number) {
    return new Promise<void>((resolve, reject) => {
        let timer: any;
        if (document.querySelectorAll(querySelector).length) return resolve();
        const observer = new MutationObserver(() => {
            if (document.querySelectorAll(querySelector).length) {
                observer.disconnect();
                if (timer !== false) clearTimeout(timer);
                return resolve();
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        if (timeout)
            timer = setTimeout(() => {
                observer.disconnect();
                reject();
            }, timeout);
    });
}

function checkForTextLoaded(searchText: string, timeout: number) {
    return new Promise<void>((resolve, reject) => {
        let timer: any;
        
        // Check if text already exists
        const walker = document.createTreeWalker(
            document.body,
            NodeFilter.SHOW_TEXT,
            null
        );
        let node: Node | null;
        while ((node = walker.nextNode())) {
            if (node.nodeValue && node.nodeValue.trim() === searchText) {
                return resolve();
            }
        }
        
        const observer = new MutationObserver(() => {
            const walker = document.createTreeWalker(
                document.body,
                NodeFilter.SHOW_TEXT,
                null
            );
            let node: Node | null;
            while ((node = walker.nextNode())) {
                if (node.nodeValue && node.nodeValue.trim() === searchText) {
                    observer.disconnect();
                    if (timer !== false) clearTimeout(timer);
                    return resolve();
                }
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        if (timeout)
            timer = setTimeout(() => {
                observer.disconnect();
                reject();
            }, timeout);
    });
}

window.CembooMediaLibrary = CembooMediaLibrary;
